import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  GetBeneficiary,
  SetSelectedBeneficiary,
  UpdateSelectedBeneficiaryFavoriteStatus,
} from '@app/finxone-web-frontend/app/lib/actions/beneficiary.action';
import { SetFormAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { ApiActions, ApiLoadingState } from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import {
  BeneficiaryState,
  BeneficiaryStateModel,
} from '@app/finxone-web-frontend/app/lib/state/beneficiary.state';
import {
  ProjectSettingsState,
  ProjectSettingsStateModel,
} from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { editBeneficiaryUnified } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/beneficiary-zone/edit-beneficiary.utils';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import { currencyToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import {
  beneficiaryDetailsPayload,
  createRedirectionUrlFromNavigationOptions,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { BeneficiaryViewType } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { beneficiarySelectionTemplate } from '@finxone-platform/shared/default-templates';
import {
  AccountInfoType,
  BaseWidgetProperties,
  ListActionValue,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  ViewType,
  ZoneActionOptions,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, catchError, map, of, takeUntil, tap } from 'rxjs';
import { GetBeneficiaryResponse } from '../../../../../lib/services/account-service/account.type';
import { PageConfigurationProvider } from '../../../../zones/page-configuration.provider';
import { FormSubmitter } from '../../../widgets/form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-beneficiary-selection',
  templateUrl: './beneficiary-selection.component.html',
  styleUrls: ['./beneficiary-selection.component.scss'],
})
export class BeneficiarySelectionComponent extends FormSubmitter implements OnChanges, OnInit, AfterViewInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  @Input() public beneficiaryData: GetBeneficiaryResponse[];
  @Input() public accountsData: AccountInfoType[];
  @Input() public currencies: Currency[];
  @Input() public itemTemplate: string = beneficiarySelectionTemplate;
  @Input() public widgetConfig = {} as UiZoneWidgetAttributeConfig;
  @Input() public override contentStyle = {} as baseCssConfigStyle;
  @Input() public viewType: ViewType = ViewType.LIST;
  @Input() public appBuilderShowFetchingContent = false;
  @Input() public selectedCurrency: string;
  @Input() public contextSwitch: boolean;
  @Output() scrolled = new EventEmitter<void>();
  @Output() beneficiarySelected = new EventEmitter<GetBeneficiaryResponse>();
  headerHeight: number;
  @Select(ApiLoadingState.getApiState(ApiActions.getBeneficiaries))
  private getBeneficiariesApiState$!: Observable<{ isLoading: boolean }>;
  @Select(BeneficiaryState.getBeneficiary)
  private beneficiaryState$!: Observable<BeneficiaryStateModel>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private templateFunction: HandlebarsTemplateDelegate<any>;
  public countryFlags: { [key: string]: string } = {};
  component: { widgetProperties: { widgetContext: { pageType: any } } };
  public containerHeight: string;
  public viewTypes = ViewType;
  public pageNumber = 1;
  public searchText: string;
  public isApiInProgress = false;
  public menuPopupCss = {
    'margin-top': '5px',
    'border-radius': '6px',
  };
  public actionOptions: ZoneActionOptions[] = [];
  public totalListCount: number;
  public projectSettings: ProjectSettingsStateModel;
  private beneficiaryViewType = BeneficiaryViewType.ALL_LIST;

  constructor(
    private el: ElementRef,
    public override store: Store,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    private router: Router,
    private accountService: AccountService,
    public injector: Injector,
  ) {
    super(store);
  }

  ngOnInit() {
    this.beneficiaryViewType =
      this.dataIn.attri.widgetProperties?.['beneficiaryViewType'] ?? this.beneficiaryViewType;

    this.getBeneficiariesApiState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isApiInProgress = result.isLoading;
    });

    this.beneficiaryState$
      .pipe(
        takeUntil(this.destroy$),
        map(({ items, favoriteItems, recentPaidItems, totalResult }) => {
          switch (this.beneficiaryViewType) {
            case BeneficiaryViewType.ALL_LIST:
              this.beneficiaryData = items;
              break;

            case BeneficiaryViewType.FAVORITE_LIST:
              this.beneficiaryData = favoriteItems;
              break;

            case BeneficiaryViewType.RECENTLY_PAID_LIST:
              this.beneficiaryData = recentPaidItems;
              break;

            default:
              this.beneficiaryData = items;
              break;
          }
          this.totalListCount = totalResult ?? 0;
        }),
      )
      .subscribe();
    if (this.widgetConfig.widgetProperties?.showFlag) this.getCountryFlagByCurrency();
    this.templateFunction = Handlebars.compile(this.itemTemplate);
    this.actionOptions = this.dataIn?.attri?.widgetProperties?.['menuOptions'];
    this.projectSettings = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings);
  }

  ngAfterViewInit() {
    this.containerHeight = scrollLoading('beneficiary-list', this.el);
  }

  ngOnChanges() {
    if (this.widgetConfig.widgetProperties?.showFlag) this.getCountryFlagByCurrency();
    const header = document.querySelector('.beneficiary-selection-header');
    this.headerHeight = header ? header.scrollHeight : 1;
  }

  public selectBeneficiary(beneficiary: GetBeneficiaryResponse): void {
    this.beneficiarySelected.emit(beneficiary);
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !this.store.selectSnapshot(BeneficiaryState.getLoadingStatus)) {
      this.scrolled.emit();
    }
  }

  public returnBeneficiaryHandleBarModel(beneficiary: GetBeneficiaryResponse) {
    return {
      ...beneficiary,
      currencyCode: beneficiary.destinationIdentifier.currency,
      currencyName: this.getCurrencyDataByKey('currency_name', beneficiary.destinationIdentifier.currency),
    };
  }

  private getCountryFlagByCurrency() {
    const widthValue = '32px';
    this.beneficiaryData.forEach((beneficiary, index: number) => {
      this.countryFlags[index] = currencyToCircleFlag(beneficiary.destinationIdentifier.currency, widthValue);
    });
  }

  private getCurrencyDataByKey(valueKey: string, currency: string) {
    return this.currencies?.find((v) => v.currency_code === currency)?.[valueKey as keyof Currency];
  }

  public searchValue(searchText: string) {
    this.searchText = searchText;
    this.pageNumber = 1;
    this.store.dispatch(
      new GetBeneficiary(
        this.pageNumber,
        this.pageSize,
        searchText,
        undefined,
        this.selectedCurrency,
        ViewType.TABLE,
        this.beneficiaryViewType,
      ),
    );
  }

  public onPageChange(event: { first: number; page: number; pageCount: number; rows: number }) {
    this.pageNumber = event.first / event.rows + 1;
    this.dataIn.attri['pageSize'] = event.rows;
    this.store.dispatch(
      new GetBeneficiary(
        this.pageNumber,
        this.pageSize,
        this.searchText,
        undefined,
        this.selectedCurrency,
        ViewType.TABLE,
        this.beneficiaryViewType,
      ),
    );
  }

  public onListSwipeAction(actionEvent: any, beneficiary: GetBeneficiaryResponse) {
    if (actionEvent?.config?.navigationOptionConfig?.navigationOption === NavigationOptionEnum.USE_AS_BLANK)
      return;

    if (actionEvent.action === ListActionValue.FAVOURITE) {
      this.updateFavoriteStatus(beneficiary?.id, !beneficiary?.isFavorite);
      return;
    }

    const data = this.mapEventToActionPayload(actionEvent.action, beneficiary?.id);
    this.performAction(data, actionEvent?.config?.navigationOptionConfig);
  }

  public handleAction(event: any) {
    const beneficiaryId = event.data?.id;

    if (
      !beneficiaryId ||
      event.actionConfig.navigationOptionConfig?.navigationOption === NavigationOptionEnum.USE_AS_BLANK
    )
      return;

    if (event.actionConfig?.action === ListActionValue.FAVOURITE) {
      this.updateFavoriteStatus(beneficiaryId, !event.data?.isFavorite);
      return;
    }

    const data = this.mapEventToActionPayload(event.actionConfig?.action, event.data?.id);
    this.performAction(data, event.actionConfig.navigationOptionConfig);
  }

  private mapEventToActionPayload(action: string | ListActionValue, uniqueId: string) {
    return {
      actionKey: action,
      actionId: uniqueId || '',
    };
  }

  private updateFavoriteStatus(beneficiaryId: string, newStatus: boolean) {
    this.store.dispatch(new SetSelectedBeneficiary(beneficiaryId));
    this.accountService
      .updateBeneficiaryFavoriteStatus(beneficiaryId, newStatus)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          // Keep the selected beneficiary favorite status in sync without refetching the full list
          this.store.dispatch(new UpdateSelectedBeneficiaryFavoriteStatus(newStatus));
        }),
        catchError((err) => {
          console.error(err);
          return of();
        }),
      )
      .subscribe();
  }

  public performAction(
    event: { actionKey: string; actionId: string; actionOption?: ZoneActionOptions },
    navigationOptionConfig?: NavigationOptionsType,
  ) {
    this.store.dispatch(new SetSelectedBeneficiary(event.actionId));
    this.setFormActionStateBeneficiary(event.actionId);
    if (event.actionKey === 'edit' || event.actionKey === 'edit-details') {
      this.initBeneficiaryEditForm(navigationOptionConfig);
      return;
    }

    let redirectUrl = this.configService.reRouteIfModal(
      event.actionOption?.zoneToNavigate || '',
      event.actionOption?.urlToNavigate || '',
    );

    if (navigationOptionConfig) {
      redirectUrl = createRedirectionUrlFromNavigationOptions(navigationOptionConfig);
    }
    this.router.navigateByUrl(redirectUrl);
  }

  private setFormActionStateBeneficiary(beneficiaryId: string) {
    const selectedBeneficiary = this.beneficiaryData.find((beneficiary) => beneficiary.id === beneficiaryId);
    if (selectedBeneficiary) {
      this.store.dispatch(
        new SetFormAction({
          type: '',
          formData: beneficiaryDetailsPayload(selectedBeneficiary),
          nextPageUrl: '',
        }),
      );
    }
  }

  private initBeneficiaryEditForm(navigationOptionConfig: NavigationOptionsType | undefined) {
    const widgetProperties = {
      zoneToNavigate: navigationOptionConfig?.zone ?? '',
      urlToNavigate: navigationOptionConfig?.pageUrl ?? '',
      textContent: '',
    } as BaseWidgetProperties;

    editBeneficiaryUnified(widgetProperties, this.injector);
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return Number(this.dataIn.attri['pageSize']);
    else return 10;
  }
}
